'use client';

import { useState } from 'react';

declare global {
  interface Window {
    _paq?: Array<unknown[]>;
  }
}

type Locale = 'fr' | 'en';

type Copy = {
  firstName: string;
  firstNamePlaceholder: string;
  email: string;
  emailPlaceholder: string;
  service: string;
  servicePlaceholder: string;
  services: Array<{ value: string; label: string }>;
  company: string;
  companyPlaceholder: string;
  phone: string;
  phonePlaceholder: string;
  budget: string;
  budgetPlaceholder: string;
  budgets: Array<{ value: string; label: string }>;
  message: string;
  messagePlaceholder: string;
  consent: string;
  submit: string;
  loading: string;
  success: string;
  error: string;
};

const COPY: Record<Locale, Copy> = {
  fr: {
    firstName: 'Prénom',
    firstNamePlaceholder: 'Marie',
    email: 'Email',
    emailPlaceholder: 'marie@entreprise.fr',
    service: 'Votre besoin',
    servicePlaceholder: 'Choisissez une prestation...',
    services: [
      { value: 'cm', label: 'Community management / gestion des réseaux' },
      { value: 'ugc', label: 'Création UGC pour ma marque' },
      { value: 'coaching', label: 'Coaching réseaux sociaux' },
      { value: 'creative-strat', label: 'Creative Strategy / stratégie créative' },
      { value: 'autre', label: 'Je ne sais pas encore' },
    ],
    company: 'Entreprise / marque (facultatif)',
    companyPlaceholder: 'Ma marque',
    phone: 'Téléphone (facultatif)',
    phonePlaceholder: '+33 6 12 34 56 78',
    budget: 'Budget indicatif (facultatif)',
    budgetPlaceholder: 'À définir ensemble',
    budgets: [
      { value: 'moins-500', label: 'Moins de 500 €' },
      { value: '500-1000', label: '500 € – 1 000 €' },
      { value: '1000-2000', label: '1 000 € – 2 000 €' },
      { value: 'plus-2000', label: 'Plus de 2 000 €' },
      { value: 'a-definir', label: 'À définir ensemble' },
    ],
    message: 'Votre projet',
    messagePlaceholder: 'Votre activité, votre objectif et ce que vous aimeriez déléguer...',
    consent: 'J’accepte que mes informations soient utilisées pour répondre à ma demande.',
    submit: 'Envoyer ma demande',
    loading: 'Envoi en cours...',
    success: 'Message reçu ! Je vous réponds sous 48 h ouvrées.',
    error: 'Le message n’a pas pu partir. Réessayez ou écrivez-moi directement.',
  },
  en: {
    firstName: 'First name',
    firstNamePlaceholder: 'Marie',
    email: 'Email',
    emailPlaceholder: 'marie@company.com',
    service: 'What do you need?',
    servicePlaceholder: 'Choose a service...',
    services: [
      { value: 'cm', label: 'Social media management' },
      { value: 'ugc', label: 'UGC creation for my brand' },
      { value: 'coaching', label: 'Social media coaching' },
      { value: 'creative-strat', label: 'Creative strategy' },
      { value: 'autre', label: 'I am not sure yet' },
    ],
    company: 'Company / brand (optional)',
    companyPlaceholder: 'My brand',
    phone: 'Phone (optional)',
    phonePlaceholder: '+33 6 12 34 56 78',
    budget: 'Indicative budget (optional)',
    budgetPlaceholder: 'To be discussed',
    budgets: [
      { value: 'moins-500', label: 'Under €500' },
      { value: '500-1000', label: '€500 – €1,000' },
      { value: '1000-2000', label: '€1,000 – €2,000' },
      { value: 'plus-2000', label: 'Over €2,000' },
      { value: 'a-definir', label: 'To be discussed' },
    ],
    message: 'Your project',
    messagePlaceholder:
      'Tell me about your business, your goal and what you would like to delegate...',
    consent: 'I agree that my information may be used to answer my request.',
    submit: 'Send my request',
    loading: 'Sending...',
    success: 'Message received! I will reply within 2 business days.',
    error: 'Your message could not be sent. Please try again or email me directly.',
  },
};

export function ContactForm({ locale }: { locale: Locale }) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const copy = COPY[locale];

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus('loading');
    const formElement = event.currentTarget;
    const formData = new FormData(formElement);
    const service = String(formData.get('service') ?? '');
    const body = {
      name: String(formData.get('firstName') ?? '').trim(),
      email: String(formData.get('email') ?? '').trim(),
      service,
      message: String(formData.get('message') ?? '').trim(),
      phone: String(formData.get('phone') ?? '').trim(),
      company: String(formData.get('company') ?? '').trim(),
      budget: String(formData.get('budget') ?? '').trim(),
      website: String(formData.get('website') ?? '').trim(),
    };

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error('contact_submit_failed');
      window._paq?.push(['trackEvent', 'Lead', 'cindy_contact_submit', service]);
      setStatus('success');
      formElement.reset();
    } catch {
      setStatus('error');
    }
  }

  if (status === 'success') {
    return (
      <div
        aria-live="polite"
        style={{
          padding: '20px 24px',
          background: 'rgba(34,197,94,0.12)',
          border: '1.5px solid rgba(34,197,94,0.35)',
          borderRadius: 14,
          color: '#4ade80',
          fontWeight: 600,
        }}
      >
        {copy.success}
      </div>
    );
  }

  return (
    <form
      data-lead-form="cindy-contact"
      onSubmit={(event) => void handleSubmit(event)}
      className="t-form"
    >
      <div
        aria-hidden="true"
        style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}
      >
        <label htmlFor="contact-website">Website</label>
        <input id="contact-website" name="website" type="text" tabIndex={-1} autoComplete="off" />
      </div>
      <div className="t-form-row">
        <div className="t-form-field">
          <label className="t-form-label" htmlFor="contact-first-name">
            {copy.firstName} *
          </label>
          <input
            id="contact-first-name"
            type="text"
            name="firstName"
            placeholder={copy.firstNamePlaceholder}
            autoComplete="given-name"
            required
            className="t-form-input"
          />
        </div>
        <div className="t-form-field">
          <label className="t-form-label" htmlFor="contact-email">
            {copy.email} *
          </label>
          <input
            id="contact-email"
            type="email"
            name="email"
            placeholder={copy.emailPlaceholder}
            autoComplete="email"
            required
            className="t-form-input"
          />
        </div>
      </div>
      <div className="t-form-field">
        <label className="t-form-label" htmlFor="contact-service">
          {copy.service} *
        </label>
        <select
          id="contact-service"
          name="service"
          required
          className="t-form-select"
          defaultValue=""
        >
          <option value="" disabled>
            {copy.servicePlaceholder}
          </option>
          {copy.services.map((service) => (
            <option key={service.value} value={service.value}>
              {service.label}
            </option>
          ))}
        </select>
      </div>
      <div className="t-form-row">
        <div className="t-form-field">
          <label className="t-form-label" htmlFor="contact-company">
            {copy.company}
          </label>
          <input
            id="contact-company"
            type="text"
            name="company"
            placeholder={copy.companyPlaceholder}
            autoComplete="organization"
            className="t-form-input"
          />
        </div>
        <div className="t-form-field">
          <label className="t-form-label" htmlFor="contact-phone">
            {copy.phone}
          </label>
          <input
            id="contact-phone"
            type="tel"
            name="phone"
            placeholder={copy.phonePlaceholder}
            autoComplete="tel"
            className="t-form-input"
          />
        </div>
      </div>
      <div className="t-form-field">
        <label className="t-form-label" htmlFor="contact-budget">
          {copy.budget}
        </label>
        <select id="contact-budget" name="budget" className="t-form-select" defaultValue="">
          <option value="">{copy.budgetPlaceholder}</option>
          {copy.budgets.map((budget) => (
            <option key={budget.value} value={budget.value}>
              {budget.label}
            </option>
          ))}
        </select>
      </div>
      <div className="t-form-field">
        <label className="t-form-label" htmlFor="contact-message">
          {copy.message} *
        </label>
        <textarea
          id="contact-message"
          name="message"
          className="t-form-textarea"
          placeholder={copy.messagePlaceholder}
          rows={4}
          required
          minLength={10}
        />
      </div>
      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          color: 'rgba(255,255,255,0.7)',
          fontSize: '0.84rem',
          lineHeight: 1.5,
        }}
      >
        <input
          type="checkbox"
          name="consent"
          required
          style={{ marginTop: 4, accentColor: '#fe7aa8' }}
        />
        <span>{copy.consent}</span>
      </label>
      {status === 'error' && (
        <div
          role="alert"
          style={{
            padding: '14px 18px',
            background: 'rgba(239,68,68,0.12)',
            border: '1.5px solid rgba(239,68,68,0.35)',
            borderRadius: 10,
            color: '#f87171',
            fontSize: '0.88rem',
          }}
        >
          {copy.error}
        </div>
      )}
      <button
        type="submit"
        disabled={status === 'loading'}
        className="t-btn-primary"
        style={{
          border: 'none',
          cursor: status === 'loading' ? 'wait' : 'pointer',
          fontFamily: 'inherit',
          opacity: status === 'loading' ? 0.7 : 1,
        }}
      >
        {status === 'loading' ? copy.loading : copy.submit}
      </button>
    </form>
  );
}
